import React from "react";
import { BCMSDefaultModel, BCMSDate, useBCMSContext, BCMSStateManagerCRUD, useStateManager, BCMSRecord, StateManager } from "bcms-react";
import Classes, { Account, CompanyAccount, PassThruHistory } from "model";
import {  WidgetContainer, WidgetView, FormContainer, FormRowView } from "components/shared";
import { PrimaryButton, SecondaryButton, UICheckbox } from "components/ui";
import DateSelect from "components/custom-ui/DateSelect";
import PendingACHPassThruTable from "../PendingACHPassThruTable";
import MobilePendingACHPassThruTable from "../MobilePendingACHPassThruTable";
import { InfoBanner } from 'components/shared/views/ConfirmBannerView';

import "./styles.css";

/**
 * @function PendingACHPassThruWidget
 * @exported true
 * @returns {React.Component}
 * @description This is the widget that shows pending ACH PassThrus on the PassThruACHPage.
 * @example
 * <PassThruACHPage>
 *   <PendingACHPassThruWidget />
 *   <HistoryACHPassThruWidget />
 * </PassThruACHPage>
 */
export default function PendingACHPassThruWidget(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    // Toggles the template form. "show" is a boolean value of `true` or `false`.
    onChange: prop => value => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", prop], value)
        .submit(setState);
    },
    onChooseFile: () => {
      const defaultFileData = data.defaultFileData

      BCMSStateManagerCRUD.init(state)
        .update(["data", "chosenFileData"], defaultFileData)
        .update(["data", "isFileChosen"], true)
        .submit(setState);
    },
    onUpload: () => {
      const defaultFileData = data.defaultFileData;
      const addedFile = PassThruHistory.init({ ...defaultFileData.record, id: PassThruHistory.nextID() });
      const mergedFileData = BCMSDefaultModel.mergeRecords(addedFile, {});

      BCMSRecord.batch([PassThruHistory.create(mergedFileData)], globalController, Classes);
      BCMSStateManagerCRUD.init(state)
        .update(["data", "chosenFileData"], {})
        .update(["data", "pending"], PassThruHistory.initAll().filter(item => item.status === "pending approval"), [])
        .update(["data", "addedFile"], addedFile)
        .update(["data", "showFileUploadedBanner"], true)
        .update(["data", "showFileUploadedTextArea"], true)
        .update(["data", "isAllCheckSelected"], false)
        .update(["data", "isFileChosen"], false)
        .submit(setState);
    },
    onCloseFileUploadedBanner: () => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "showFileUploadedBanner"], false)
        .submit(setState);
    },
    onShowRawContent: () => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "showFormattedContent"], false)
        .submit(setState);
    },
    onShowFormattedContent: () => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "showFormattedContent"], true)
        .submit(setState);
    },
    onPassThruACHDetailOpenModalClick: () => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "passThruACHDetailsModalIsOpen"], true)
        .submit(setState);
    },
    onPassThruACHDetailCloseModalClick: () => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "passThruACHDetailsModalIsOpen"], false)
        .update(["data", "showFormattedContent"], true)
        .submit(setState);
    },
    onSelectAllCheckmarksChange: value => {
      let pending =  data.pending
      pending = pending.reduce((acc, pending) => {
        pending.isChecked = value;
        acc.push(pending);
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
        .update(["data", "isAllCheckSelected"], value)
        .update([ "data", "pending" ], pending)
        .submit(setState);
    },
    onRowChecked: id => value => {
      let pending = data.pending
      let isAllRowsChecked = false;
      // Updated the "isChecked" property.
      pending = pending.reduce((acc, pending) => {
        if (pending.id === id) {
          pending.isChecked = value;
        }
        acc.push(pending);
        return acc;
      }, []);

      isAllRowsChecked = pending.filter(item => item.isChecked === true, []).length === pending.length ? true : false;

      BCMSStateManagerCRUD.init(state)
        .update(["data", "pending"], pending)
        .update(["data", "isAllCheckSelected"], isAllRowsChecked)
        .submit(setState);
    },
  });

  return (
    <StateManager state={{ data, ui }} controller={controller}>
      <WidgetContent />
    </StateManager>
  );
}

export const getDefaultData = () => ({
  chosenFileData: {},
  defaultFileData: PassThruHistory.init({
    createdBy: "George Washington",
    createdDate: "0",
    updatedDate: "",
    status: "pending approval",
    isChecked: false,
    offsetAccount: Account.initByID(6),
    companyAccount: CompanyAccount.initByID(0),
    participants: "0",
    creditAmount: "0",
    debitAmount: "100",
    secCode: "CCD",
    batchNumber: "0000260",
    rawFileName: "9202011252P_SAMPLEPASSTHRU",
    fileName: "P_samplePassThru.txt",
    fileURL: ""
  }),
  pending: PassThruHistory.initAll().filter(item => item.status === "pending approval"),
  overridePastEffectiveDates: false,
  processAsThirdParty: false,
  selectedDate: BCMSDate.init().nextBusinessDay(),
  skipInvalidEffectiveDates: false,
  skipDuplicateCheck: false,
  showFileUploadedBanner: false,
  showFileUploadedTextArea: false,
  isFileChosen: false,
  showFormattedContent: true,
  passThruACHDetailsModalIsOpen: false,
  isAllCheckSelected: false
});
export const getDefaultUI = () => ({
  // no ui changes for now
});

function WidgetContent(props) {
  const { data, ui, onChange, onChooseFile, onUpload, onCloseFileUploadedBanner,
    onShowFormattedContent, onShowRawContent, onPassThruACHDetailOpenModalClick, onPassThruACHDetailCloseModalClick,
    onSelectAllCheckmarksChange, onRowChecked } = useStateManager();
    
  const chosenFileData = data.chosenFileData ?? {};
  const fileNameText = chosenFileData.fileName ? chosenFileData.fileName : "No file chosen";
  const actions = {
    onShowFormattedContent,
    onShowRawContent,
    onPassThruACHDetailOpenModalClick,
    onPassThruACHDetailCloseModalClick,
    onSelectAllCheckmarksChange,
    onRowChecked
  }
  // Only business days after today are enabled.
  const disableDay = date => {
    let tomorrow = BCMSDate.init();
    tomorrow.offset(1);
    let disabled = date.isBefore(tomorrow);
    if (date.isWeekend()) { disabled = true; }
    return disabled;
  };

  return (
    <WidgetContainer className="pending-ach-pass-thru-widget">
      {data.showFileUploadedBanner && <InfoBanner text="The file was uploaded successfully." onClick={onCloseFileUploadedBanner} />}
      <WidgetView text="Pending ACH Pass-Thru" dataPoint="Pending_Title">
        {data.showFileUploadedTextArea &&
          <textarea className="file-uploading-textarea" readOnly defaultValue={
            `* Processing file "${data.addedFile.fileName}"... \n* The file will be uploaded as "${data.addedFile.fileName}". `
          }>
          </textarea>
        }
        <FormContainer className="small-form-width">
          <FormRowView className="dropdown-checkbox-row">
            <UICheckbox label="Override Past Effective Dates" value={data.overridePastEffectiveDates ? "checked" : ""}
              onChange={onChange("overridePastEffectiveDates")} disabled={data.skipInvalidEffectiveDates} dataPoint="Override_Past_Effective_Dates"/>
            {data.overridePastEffectiveDates &&
              <div className="hidden-content">
                <DateSelect value={data.selectedDate} disableDay={disableDay} />
              </div>
            }
          </FormRowView>
          <FormRowView className="invalid-dates-checkbox-row">
            <UICheckbox label="Skip Invalid Effective Dates" value={data.skipInvalidEffectiveDates ? "checked" : ""}
              onChange={onChange("skipInvalidEffectiveDates")} disabled={data.overridePastEffectiveDates} dataPoint="Skip_Invalid_Effective_Dates"/>
          </FormRowView>
          <FormRowView className="third-party-checkbox-row">
            <UICheckbox label="Process as Third Party" value={data.processAsThirdParty ? "checked" : ""}
              onChange={onChange("processAsThirdParty")} disabled={false} dataPoint="Process_As_Third_Party" />
          </FormRowView>
          <FormRowView className="skip-duplicate-check-row">
            <UICheckbox label="Skip Duplicate Check" value={data.skipDuplicateCheck ? "checked" : ""}
              onChange={onChange("skipDuplicateCheck")} disabled={false} dataPoint="Skip_Duplicate_Check" />
          </FormRowView>
          <FormRowView text="Maximum file size: 500 KB" className="choose-file-row">
            <SecondaryButton text="Choose File" onClick={onChooseFile} dataPoint="Choose_File_Button"/>
          </FormRowView>
          <FormRowView text={fileNameText} className="upload-file-row">
            <PrimaryButton className={!data.isFileChosen ? "disabled-button" : ""} text="Upload" onClick={onUpload}  dataPoint="Upload_Button"/>
          </FormRowView>
        </FormContainer>
        <div className="show-on-desktop">
          <PendingACHPassThruTable data={data} actions={actions} />
        </div>
        <div className="show-on-mobile">
          <MobilePendingACHPassThruTable data={data} actions={actions} />
        </div>
      </WidgetView>
    </WidgetContainer>
  );
}